import { useEffect, useRef, useState, useCallback } from "react";
import { jarvisChat, jarvisTTS } from "@/lib/api";

const WAKE_WORDS = [
  // Feb 2026 (owner Jul 23): Wake word standardised to "raw company" for
  // every user who downloads the app. Common phonetic neighbours are
  // included so mumbled / accented pronunciation still fires. All the
  // older wake phrases ("mate", "jarvis", "raw data" etc.) were removed
  // so new users have exactly ONE thing to remember.
  "raw company", "rawcompany",
  "hey raw company", "ok raw company", "hi raw company",
  "hello raw company", "alright raw company", "morning raw company",
  // Phonetic neighbours — speech recognition often mis-hears "raw" as
  // "row" / "ro" and "company" as "come pany" or "compnee":
  "row company", "ro company", "raw compnee", "raw come pany",
];
// PUBLIC WAKE WORD — the one line users are told to say.
export const PUBLIC_WAKE_WORD = "Raw Company";
const COMMAND_SILENCE_MS = 2200;
const MAX_COMMAND_MS = 9000;

export default function useJarvis({ onTranscript, onReply, onAction, onAudioElement }) {
  const [listening, setListening] = useState(false);
  // Always-on: mic auto-arms on app open and stays armed for the session.
  // Survives page reloads, tab switches, focus/blur, and transient mic errors.
  const [wakeArmed, setWakeArmedState] = useState(true);
  // 🔇 MUTE — owner asked: "I need a button around the orb to mute it so
  // it stops listening to me." When muted, recognition is paused entirely
  // so the assistant can't fire on background chatter. Persisted.
  const [muted, setMutedState] = useState(() => {
    try { return localStorage.getItem("rawco-mic-muted") === "1"; } catch { return false; }
  });
  const mutedRef = useRef(false);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const [thinking, setThinking] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [micStatus, setMicStatus] = useState("idle"); // idle | granted | denied | error

  const recogRef = useRef(null);
  const audioRef = useRef(null);
  const wakeArmedRef = useRef(true);
  const activeRef = useRef(false);
  const commandStartRef = useRef(0);
  const silenceTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const lastSubmittedRef = useRef("");
  // Monotonic request ID — every sendToBrain bumps this. When a reply
  // arrives, we only speak it if its ID still matches the latest. This
  // kills the "click twice → two voices talking over each other" bug.
  const reqIdRef = useRef(0);
  // 🔇 BARGE-IN SUPPRESSION + 🎤 FOLLOW-UP LISTENING
  // Mirror `speaking` into a ref so the SpeechRecognition `onresult`
  // closure (created once at start) can read the LATEST value. Without
  // this, the mic happily re-triggers on Jarvis's own voice coming out
  // of the speakers when the spoken reply contains a wake-word
  // homophone ("raw eggs", "jarvis-style", etc.) → cancels current
  // speech → starts a new command → infinite chop loop. Owner's
  // complaint: "two voices keep talking over each other, stopping
  // halfway through sentences".
  const speakingRef = useRef(false);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  // Follow-up listening window: when Jarvis finishes speaking, the mic
  // stays hot for FOLLOW_UP_MS so the owner can reply WITHOUT saying
  // "Raw Company" again. Owner asked: "after the robot has finished
  // talking, can it leave the mic open so I can reply back to his
  // sentence rather than having to say Raw Company every time?".
  const FOLLOW_UP_MS = 7000;
  const followUpUntilRef = useRef(0);
  // 🔊 Last spoken reply — used by onresult to reject echo/tail bleed
  // from the speaker back into the mic. Owner Feb 2026: "robot keeps
  // getting stuck repeating itself and not working again." Root cause:
  // during the FOLLOW_UP_MS window (7s after Jarvis finishes speaking)
  // the mic often picks up the audio tail / reverb and treats it as a
  // follow-up command → same reply is generated → loops forever.
  const lastSpokenReplyRef = useRef("");
  useEffect(() => {
    // The moment speaking flips from true → false, open the follow-up
    // window. The brain-reply path can extend the window manually too.
    if (!speaking) {
      followUpUntilRef.current = Date.now() + FOLLOW_UP_MS;
    } else {
      // While Jarvis is speaking, suppress the window (barge-in guard).
      followUpUntilRef.current = 0;
    }
  }, [speaking]);

  // Kept for backward-compat with any caller passing false. Forced to ignore "off".
  const setWakeArmed = useCallback((next) => {
    const v = typeof next === "function" ? next(wakeArmedRef.current) : next;
    // The user wants 24/7 listening — refuse to disarm
    if (!v) return;
    wakeArmedRef.current = true;
    setWakeArmedState(true);
  }, []);

  useEffect(() => { wakeArmedRef.current = wakeArmed; }, [wakeArmed]);

  // 🔇 MUTE setter — stops/starts recognition immediately + persists.
  const setMuted = useCallback((next) => {
    setMutedState((cur) => {
      const v = typeof next === "function" ? next(cur) : next;
      mutedRef.current = !!v;
      try { localStorage.setItem("rawco-mic-muted", v ? "1" : "0"); } catch { /* ignore */ }
      try {
        const recog = recogRef.current;
        if (v) { recog?.stop?.(); }
        else if (wakeArmedRef.current) { recog?.start?.(); }
      } catch { /* ignore — start can throw "already started" */ }
      return !!v;
    });
  }, []);

  // 🔓 Global first-interaction audio unlock. Browser autoplay policy blocks
  // `new Audio(...).play()` until the user has interacted with the page.
  // The morning brief and proactive eye announcements can fire BEFORE the
  // user has pressed the mic button, so we install a one-time listener on
  // ANY click / keydown / touch and prime the audio pipeline. After this
  // fires once, all subsequent ElevenLabs TTS plays cleanly.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.__rcAudioUnlocked) return undefined;
    const unlock = () => {
      if (window.__rcAudioUnlocked) return;
      window.__rcAudioUnlocked = true;
      try {
        const a = new Audio("data:audio/mpeg;base64,SUQzAwAAAAAAFlRTU0UAAAAMAAADTGF2ZjU3LjcxLjEwMP/7kGQAAAAAAAAAAAAAAAAAAAAAAAAA");
        a.volume = 0;
        a.play().catch(() => {});
      } catch { /* ignore */ }
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          if (ctx.state === "suspended") ctx.resume().catch(() => {});
          window.__rcAudioCtx = ctx;
        }
      } catch { /* ignore */ }
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
      window.removeEventListener("touchstart", unlock, true);
    };
    window.addEventListener("pointerdown", unlock, true);
    window.addEventListener("keydown", unlock, true);
    window.addEventListener("touchstart", unlock, true);
    return () => {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
      window.removeEventListener("touchstart", unlock, true);
    };
  }, []);

  // --- Mic permission helper ---
  const requestMicPermission = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicStatus("error");
        return false;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop tracks immediately; we only needed the permission prompt
      stream.getTracks().forEach(t => t.stop());
      setMicStatus("granted");
      return true;
    } catch (e) {
      console.warn("[Jarvis] mic permission denied:", e?.name || e);
      setMicStatus("denied");
      return false;
    }
  }, []);

  // --- Helpers ---------------------------------------------------------------
  const extractCommandAfterWake = (text) => {
    const t = (text || "").toLowerCase();
    let idx = -1, hitLen = 0;
    for (const w of WAKE_WORDS) {
      const i = t.indexOf(w);
      if (i >= 0 && (idx === -1 || i < idx)) { idx = i; hitLen = w.length; }
    }
    if (idx === -1) return null;
    return text.slice(idx + hitLen).trim().replace(/^[,.!?\s]+/, "");
  };

  const speakBrowser = useCallback((text) => {
    if (!("speechSynthesis" in window)) {
      console.warn("[Jarvis] speechSynthesis not in window");
      setSpeaking(false); window.__rcoJarvisSpeaking = false;
      return;
    }

    const synth = window.speechSynthesis;

    // iOS Safari workaround: cancel() then immediate speak() in same tick.
    // resume() kicks it if the audio pipeline was suspended by the OS.
    const fire = () => {
      try {
        const u = new SpeechSynthesisUtterance(text);
        u.rate = 1.0; u.pitch = 0.9; u.volume = 1.0; u.lang = "en-GB";
        const voices = synth.getVoices() || [];
        if (voices.length) {
          const british = voices.find(v => /en-GB/i.test(v.lang) && /male|daniel|google uk english male/i.test(v.name))
                       || voices.find(v => /en-GB/i.test(v.lang));
          const anyMale = voices.find(v => /male|daniel|alex/i.test(v.name));
          const picked = british || anyMale || voices.find(v => /^en[-_]/i.test(v.lang)) || voices[0];
          if (picked) u.voice = picked;
        }
        u.onend = () => { setSpeaking(false); window.__rcoJarvisSpeaking = false; };
        u.onerror = (ev) => { console.warn("[Jarvis] browser TTS error:", ev?.error); setSpeaking(false); window.__rcoJarvisSpeaking = false; };
        synth.cancel();
        // tiny delay needed on Chrome to avoid the cancel() suppressing the speak()
        setTimeout(() => {
          synth.speak(u);
          // Chrome bug: after 15s of speech, it pauses. Periodic resume kicks it.
          const kicker = setInterval(() => {
            if (!synth.speaking) return clearInterval(kicker);
            synth.pause(); synth.resume();
          }, 12000);
          u.onend = () => { clearInterval(kicker); setSpeaking(false); window.__rcoJarvisSpeaking = false; };
        }, 30);
      } catch (e) {
        console.warn("[Jarvis] speakBrowser threw:", e);
        setSpeaking(false); window.__rcoJarvisSpeaking = false;
      }
    };

    // Voices load asynchronously on first hit. Wait for them on first call.
    if (!synth.getVoices().length) {
      const onVoices = () => {
        synth.removeEventListener("voiceschanged", onVoices);
        fire();
      };
      synth.addEventListener("voiceschanged", onVoices);
      // Safety: if voiceschanged never fires (some Chromiums), still fire after 250ms
      setTimeout(() => { synth.removeEventListener("voiceschanged", onVoices); fire(); }, 250);
    } else {
      fire();
    }
  }, []);

  const speak = useCallback(async (text, voiceId) => {
    if (!text) return;
    // 🔒 GLOBAL VOICE LOCK — when SwarmChat is in the middle of its
    // sequential agent playback we MUST NOT cancel it by bumping reqId.
    // Owner complaint: "agents are fighting, none finish a sentence".
    // Wait up to 30s for the swarm chain to finish; if still busy, give up
    // silently so the orb doesn't deadlock.
    if (window.__rcoSwarmVoiceBusy) {
      const start = Date.now();
      while (window.__rcoSwarmVoiceBusy && (Date.now() - start) < 30000) {
        await new Promise(r => setTimeout(r, 200));
      }
      if (window.__rcoSwarmVoiceBusy) return; // gave up — don't override
    }
    // Bump the request ID so any in-flight speech gets superseded by this
    // new one. Same lock used by sendToBrain — guarantees only ONE voice
    // playing at a time even when multiple components call speak() at once.
    reqIdRef.current += 1;
    const myReqId = reqIdRef.current;
    // CRITICAL: cancel any in-flight speech BEFORE starting the new one.
    // Fixes "talks over itself when I click two things at once" by ensuring
    // only one audio stream is ever playing.
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    } catch {}
    // 🚨 Set BOTH the ref AND the state. The ref propagates synchronously
    // (so `recog.onresult` sees `speakingRef.current === true` on the very
    // next tick — no 16ms render-commit gap where the mic can self-trigger
    // on speaker bleed). The state is for UI badges.
    speakingRef.current = true;
    setSpeaking(true);
    // 🌐 Window-level flag so other components (EyesButton's keep-watching
    // poller, etc) can check whether Jarvis is mid-speech and stay quiet.
    // Owner complaint: "eyes keeps explaining every 3 seconds and talking
    // over me, not finishing sentences".
    // eslint-disable-next-line no-undef
    window.__rcoJarvisSpeaking = true;
    // Capture the current reply so onresult can reject any transcript
    // that's a substring / heavy overlap of it during the follow-up
    // window (echo/tail-bleed detection).
    lastSpokenReplyRef.current = String(text || "").toLowerCase().trim();
    // 🛟 Safety watchdog: if onended/onerror never fire (audio element
    // dies silently, network hiccup, or any future bug), force-clear the
    // "speaking" flag so the orb doesn't get stuck on SPEAKING forever.
    // Cut from 90s → 20s Feb 2026 — owner kept hitting "robot frozen
    // saying speaking but nothing's playing" and 90s of waiting is
    // unusable. 20s comfortably covers a 4-paragraph TTS reply with
    // network slack but unsticks the UI fast when something genuinely
    // dies upstream.
    const myReqIdForWatchdog = myReqId;
    const watchdog = setTimeout(() => {
      if (reqIdRef.current === myReqIdForWatchdog) {
        console.warn("[Jarvis] speak() watchdog: forcing SPEAKING off after 20s");
        speakingRef.current = false;
        setSpeaking(false); window.__rcoJarvisSpeaking = false;
      }
    }, 20000);
    let usedElevenLabs = false;
    try {
      // Forward voiceId (e.g. Tom's onwK4e9..., Sarah's XB0fDUn...) to TTS so
      // the agent speaks in their OWN ElevenLabs voice. Was being silently
      // dropped before — that's why "ask Tom" worked but Tom never spoke.
      const r = await jarvisTTS(text, voiceId);
      // If another speak() has fired while we were waiting on TTS bytes,
      // bail out — don't start playback for stale audio.
      if (myReqId !== reqIdRef.current) { clearTimeout(watchdog); setSpeaking(false); window.__rcoJarvisSpeaking = false; return; }
      if (r && r.audio) {
        const audio = new Audio(r.audio);
        audioRef.current = audio;
        onAudioElement?.(audio);
        audio.onended = () => {
          clearTimeout(watchdog);
          // Hold the barge-in guard 900ms after the audio ends — the
          // speakers have a long tail of ambient hum/reverb that the
          // mic could pick up as follow-up echo, causing the "robot
          // keeps repeating itself" bug. Bumped 400ms → 900ms Feb 2026
          // — combined with the echo-reject logic in onresult, this
          // eliminates the self-hearing loop.
          setTimeout(() => { speakingRef.current = false; setSpeaking(false); window.__rcoJarvisSpeaking = false; }, 900);
        };
        // CRITICAL: do NOT fall back to browser TTS on mid-playback errors.
        // If the ElevenLabs stream hiccups, we lose the rest of the sentence —
        // BUT we never want the wrong-voice (browser default) firing over it.
        // User complaint: "woman talks first, then my man talks over her".
        audio.onerror = () => {
          clearTimeout(watchdog);
          setTimeout(() => { speakingRef.current = false; setSpeaking(false); window.__rcoJarvisSpeaking = false; }, 900);
        };
        try {
          await audio.play();
          usedElevenLabs = true;
          return;
        } catch (playErr) {
          // Autoplay blocked → only NOW fall through to browser TTS
          console.warn("[Jarvis] audio.play() blocked, falling back to browser TTS", playErr?.name);
        }
      } else if (r && r.fallback === "browser") {
        if (!window.__rcEllabsWarned) {
          window.__rcEllabsWarned = true;
          console.warn("[Jarvis] ElevenLabs unavailable — using browser voice. Reason:", (r.error || "").slice(0, 200));
        }
      }
    } catch (e) {
      console.warn("[Jarvis] TTS error, using browser fallback", e?.message);
    }
    // Browser fallback ONLY when ElevenLabs path didn't succeed. The
    // browser TTS path manages its own setSpeaking(false) via onend, so we
    // hand off the watchdog to it.
    if (!usedElevenLabs) {
      clearTimeout(watchdog);
      speakBrowser(text);
    }
  }, [onAudioElement, speakBrowser]);

  const sendToBrain = useCallback(async (text, intentHint, opts = {}) => {
    if (!text || !text.trim()) return;
    const silentOnError = !!opts.silentOnError;
    const norm = text.trim().toLowerCase();
    if (norm === lastSubmittedRef.current) return;
    lastSubmittedRef.current = norm;
    setTimeout(() => { if (lastSubmittedRef.current === norm) lastSubmittedRef.current = ""; }, 3000);

    // 🛡 BRIEF GLOBAL LOCK — applies to ALL paths (voice + auto). Owner
    // complaint: "brief still cuts out halfway and starts again from the
    // beginning". Root cause: voice commands of "morning brief" skipped
    // the previous lock. Now any morning_brief request within 5 min of
    // the last one is silently short-circuited. Lock lives on `window`
    // so it survives React re-renders and is shared between this hook
    // and the App.js auto-triggers.
    const isMorningBrief = intentHint === "morning_brief" ||
      /morning\s+brief|give\s+me\s+my\s+brief|run\s+my\s+brief|read\s+my\s+brief/i.test(norm);
    if (isMorningBrief) {
      // eslint-disable-next-line no-undef
      const sinceLast = Date.now() - Number(window.__rcoLastBriefAt || 0);
      if (sinceLast < 300000) {
        console.warn(`[Jarvis] brief lock active (${Math.round(sinceLast/1000)}s since last); ignoring duplicate brief request`);
        return;
      }
      // eslint-disable-next-line no-undef
      window.__rcoLastBriefAt = Date.now();
    }

    // 🔌 SHELLY PLUG: previously intercepted here via regex as a stopgap
    // while the brain didn't know about plugs. As of Feb 2026 the backend
    // brain has a native `toggle_plug` intent (server.py:execute_action),
    // so we let the request flow through the normal /api/jarvis/chat
    // pipeline. Benefit: same JSON action shape as everything else,
    // logged in hardware_wakes for diagnostics, fuzzy plug-name matching,
    // and the spoken reply runs through ElevenLabs (not just speak()).

    // Bump the request ID. Any older request's reply will be ignored when it
    // comes back. Also kill anything currently speaking so the previous
    // request's audio doesn't bleed into this one.
    reqIdRef.current += 1;
    const myReqId = reqIdRef.current;
    try {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    } catch { /* ignore */ }
    setSpeaking(false); window.__rcoJarvisSpeaking = false;

    // Instant audible acknowledgement — soft chime so the owner knows the
    // command was received while GPT spins up (typically 4-8s).
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        const now = ctx.currentTime;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        o.connect(g); g.connect(ctx.destination);
        o.start(now); o.stop(now + 0.25);
        setTimeout(() => { try { ctx.close(); } catch {} }, 400);
      }
    } catch {}

    setThinking(true);
    onTranscript?.({ role: "user", text });
    try {
      const r = await jarvisChat(text, undefined, intentHint);
      // If a newer request has been fired while we were waiting, throw
      // this stale reply away — don't speak it, don't show it. This is the
      // "click twice → two voices" fix.
      if (myReqId !== reqIdRef.current) return;
      onReply?.({ role: "jarvis", text: r.reply, action: r.action, suggested_replies: r.suggested_replies });
      if (r.action) onAction?.(r.action, r.reply);
      // 📮 Post-classification wake dispatch — see the (Removed) comment
      // upstream. If the LLM classified the utterance as a bare greeting
      // (owner just said "hi mate" / "hey mate" with nothing else), NOW
      // is a safe moment to tell AgentInbox to speak its notification
      // summary — we won't step on the user's own speech because they
      // clearly weren't giving a real command.
      if (r.action?.type === "greeting" || r.action?.type === "chit_chat") {
        try { window.dispatchEvent(new CustomEvent("rawco-wake-detected", { detail: { text: r.reply || "" } })); } catch (_e) { /* ignore */ }
      }
      // If the action result carries a spoken_note (e.g. "this is a mock trade"),
      // append it to the spoken reply so the owner ALWAYS hears mock warnings.
      const note = r.action?.result?.spoken_note;
      const spoken = note ? `${r.reply || ""} ${note}`.trim() : r.reply;
      // 🚫 When the brain emits talk_to_agent, the AGENT speaks in their own
      // voice via handleAction → jarvis.speak(reply, voice_id). We MUST NOT
      // also speak the brain's "putting you through to Tom" line here —
      // because (per the reqId dedup) that would cancel Tom mid-sentence
      // and the owner would hear nothing. Skip the Jarvis voice entirely.
      const isAgentHandoff = r.action?.type === "talk_to_agent";
      if (!isAgentHandoff) await speak(spoken);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      if (silentOnError) {
        console.warn("[Jarvis] silent boot-time chat failure:", e?.message);
        return;
      }
      // 🛡 Rate-limit the "reasoning core unreachable" voice. Owner
      // complaint: "reasoning core unreachable is repeating over and over".
      // If we've already complained in the last 30s, stay silent — just
      // surface the error visually via onReply so the owner still sees it.
      const errMsg = "Apologies sir, my reasoning core is unreachable.";
      const now = Date.now();
      // eslint-disable-next-line no-undef
      const last = Number(window.__rcoLastReasoningErrorAt || 0);
      onReply?.({ role: "jarvis", text: errMsg, action: null });
      if (now - last > 30000) {
        // eslint-disable-next-line no-undef
        window.__rcoLastReasoningErrorAt = now;
        await speak(errMsg);
      } else {
        console.warn("[Jarvis] suppressed repeat reasoning-core voice");
      }
    } finally {
      setThinking(false);
    }
  }, [onAction, onReply, onTranscript, speak]);

  const finaliseCommand = useCallback((rawText) => {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    activeRef.current = false;
    setListening(false);
    setTranscript("");
    // 🛡 Bump the dedupe pointer so the next onresult event ignores
    // everything we've already processed (kills "voice doubles" bug).
    try {
      const recog = recogRef.current;
      if (recog && typeof recog.__resultsLen === "number") {
        recog.__consumedUpTo = recog.__resultsLen;
        // Mirror to closure via custom property already set in onresult
      }
    } catch {}
    const cmd = extractCommandAfterWake(rawText) || rawText;
    if (cmd && cmd.trim()) sendToBrain(cmd.trim());
  }, [sendToBrain]);

  // --- Recognition lifecycle -------------------------------------------------
  const startRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { console.warn("[Jarvis] SpeechRecognition not supported in this browser"); setMicStatus("error"); return; }
    try { recogRef.current?.stop(); } catch {}

    const recog = new SR();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = "en-GB";

    let lastFullText = "";
    // 🛡 DEDUPE GUARD — tracks how many recognition results we've already
    // processed. Without this, after finaliseCommand the next onresult
    // event still sees the OLD utterance in e.results and re-fires the
    // wake-word check, causing the same command to be sent twice.
    let consumedUpTo = 0;

    const armSilenceTimer = (textAtArm) => {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        finaliseCommand(lastFullText || textAtArm);
      }, COMMAND_SILENCE_MS);
    };

    recog.onstart = () => setMicStatus("granted");

    recog.onresult = (e) => {
      let interim = "", finals = "";
      // Only consume results NEW since the last command finalised. This
      // kills the "hears everything twice" bug.
      const startIdx = Math.max(e.resultIndex || 0, recog.__consumedUpTo || 0, consumedUpTo);
      for (let i = startIdx; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finals += " " + t;
        else interim += " " + t;
      }
      const combined = (finals + " " + interim).trim();
      lastFullText = combined;
      setTranscript(combined);

      // Expose for finaliseCommand to bump consumedUpTo (see below).
      recog.__resultsLen = e.results.length;

      // 🛟 LISTENING WATCHDOG — if `listening` somehow stays true for
      // >15s, force a reset so the orb doesn't get stuck. Owner
      // complaint: "stuck on listening now and frozen screen". Runs
      // BEFORE the barge-in guard so it works even during speaker
      // bleed-through.
      if (activeRef.current && Date.now() - commandStartRef.current > 15000) {
        console.warn("[Jarvis] listening watchdog: force-clearing stuck listening state");
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
        activeRef.current = false;
        setListening(false);
        setTranscript("");
        return;
      }

      // 🔇 BARGE-IN GUARD — while Jarvis is speaking, the speakers can leak
      // into the mic and trigger self-loops (recipe says "raw eggs" →
      // recog fires on "raw" → cancels current speech → starts new
      // command → chops mid-sentence). Drop ALL results while speaking.
      if (speakingRef.current) {
        return;
      }
      // 🔇 MUTE GATE — owner pressed the mute button. Drop everything.
      if (mutedRef.current) {
        return;
      }

      const lower = combined.toLowerCase();
      const hasWake = WAKE_WORDS.some(w => lower.includes(w));
      // 🎤 FOLLOW-UP LISTENING — if Jarvis just finished speaking,
      // accept the owner's reply WITHOUT requiring the wake word for
      // FOLLOW_UP_MS seconds. Outside that window, behaviour falls back
      // to the normal wake-word gate.
      const inFollowUp = Date.now() < followUpUntilRef.current;

      // 🔊 ECHO-REJECT — reject follow-up commands that are clearly the
      // speaker's tail-bleed of Jarvis's own reply. Owner Feb 2026:
      // "robot keeps getting stuck repeating itself" — root cause was
      // the mic picking up the last 1-3 words of the TTS audio and
      // sending them straight back as the "next command". If the
      // transcript is a substring of the last reply OR contains a
      // 4+ word sequence lifted from it, treat it as echo and drop.
      if (inFollowUp && !hasWake && lastSpokenReplyRef.current) {
        const reply = lastSpokenReplyRef.current;
        const cmd = lower.trim();
        // Substring check — fast + kills most tail bleeds.
        if (cmd.length >= 3 && (reply.includes(cmd) || cmd.length < 20 && cmd.split(/\s+/).length < 4)) {
          // Also drop very short (< 20 chars) fragments during follow-up
          // — the mic almost always picks up 1-3 word audio tails, and
          // real follow-up commands are almost always longer.
          return;
        }
        // N-gram check — reject if a 4-word sequence of the transcript
        // appears verbatim in the last reply.
        const cmdWords = cmd.split(/\s+/).filter(Boolean);
        if (cmdWords.length >= 4) {
          for (let i = 0; i <= cmdWords.length - 4; i++) {
            const gram = cmdWords.slice(i, i + 4).join(" ");
            if (reply.includes(gram)) return;
          }
        }
      }

      if (hasWake || inFollowUp) {
        if (!activeRef.current) {
          activeRef.current = true;
          commandStartRef.current = Date.now();
          setListening(true);
          // 📮 (Removed) — we USED to dispatch 'rawco-wake-detected' here
          // to trigger AgentInbox's wake greeting. But that fired an
          // ElevenLabs TTS mid-utterance, which killed Web Speech
          // Recognition mid-command → only "hi mate" reached the backend
          // → owner heard the generic "Good morning, sir" greeting
          // instead of the task being executed. The wake-greeting is now
          // dispatched from finaliseCommand ONLY when the backend
          // classified the utterance as a bare `greeting` (i.e. user
          // said just "hi mate" with no task), so we never step on the
          // user's own speech.
        }
        if (Date.now() - commandStartRef.current > MAX_COMMAND_MS) {
          finaliseCommand(combined);
          return;
        }
        armSilenceTimer(combined);
      }
    };

    recog.onend = () => {
      if (activeRef.current) finaliseCommand(lastFullText);
      lastFullText = "";
      setTranscript("");
      // Auto-restart while armed (with backoff if there were errors)
      // Skip restart entirely while muted — owner is explicitly silencing.
      if (mutedRef.current) return;
      if (wakeArmedRef.current && micStatus !== "denied") {
        // 🛡 Debounce: if there's already a restart queued, don't pile up
        // another one. Without this, multiple onend events from competing
        // recognition instances stack restarts → CPU spike.
        if (restartTimerRef.current) return;
        restartTimerRef.current = setTimeout(() => {
          restartTimerRef.current = null;
          try { recog.start(); } catch (e) { /* already running */ }
        }, 600);
      }
    };

    recog.onerror = (e) => {
      // 🛡 Throttle the "aborted" / "no-speech" noise. These fire dozens
      // of times per minute under normal use (every silence gap, every
      // visibility flip) and the previous unthrottled console.warn was
      // CPU-pegging DevTools at 475+ logs/sec and freezing the browser.
      const code = e.error;
      const benign = code === "aborted" || code === "no-speech" || code === "audio-capture";
      const now = Date.now();
      if (!benign) console.warn("[Jarvis] recog error:", code);
      else if (now - (recog.__lastBenignWarnAt || 0) > 30000) {
        console.warn("[Jarvis] recog error (throttled):", code);
        recog.__lastBenignWarnAt = now;
      }
      if (code === "not-allowed" || code === "service-not-allowed") {
        // DO NOT auto-disarm any more — surface a clear status instead so the
        // user can grant permission. The user can hit the mic button to retry.
        setMicStatus("denied");
        clearTimeout(restartTimerRef.current);
      }
      // "no-speech", "audio-capture", "network", "aborted" — let onend's restart handle it
    };

    recogRef.current = recog;
    try { recog.start(); } catch (e) { /* may already be running */ }
  }, [finaliseCommand, micStatus]);

  useEffect(() => {
    if (wakeArmed) {
      // Ask for mic permission FIRST so the browser shows a prompt rather than
      // silently failing inside SpeechRecognition.
      (async () => {
        const ok = await requestMicPermission();
        if (!ok) return; // do not enter the restart loop
        const t = setTimeout(() => startRecognition(), 200);
        return () => clearTimeout(t);
      })();

      // Always-on resilience: re-start recognition whenever the tab regains
      // focus / visibility, in case the OS suspended the audio pipeline.
      // 🛡 Throttle: only act if we haven't poked the recogniser in the
      // last 5s — prevents the rapid focus/blur dance (e.g. dev-tools
      // open, OS notifications) from spawning a stack of competing
      // SpeechRecognition instances that fight each other with
      // "aborted" errors for hundreds of cycles.
      let lastVisibilityKickAt = 0;
      const onVisible = () => {
        if (document.visibilityState !== "visible" || !wakeArmedRef.current) return;
        const now = Date.now();
        if (now - lastVisibilityKickAt < 5000) return;
        lastVisibilityKickAt = now;
        // small delay so the browser releases any held resources
        setTimeout(() => {
          try { recogRef.current?.stop(); } catch {}
          startRecognition();
        }, 300);
      };
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("focus", onVisible);

      // 🛟 GLOBAL LISTENING WATCHDOG — fires every 2s. If we've been
      // stuck on "listening" for > 15s without finalising, force-clear
      // it. Covers the case where recog.onresult never fires again
      // (OS-level stall, mic stream dropped, etc.) so the orb can
      // never get permanently frozen on "LISTENING".
      const stuckWatch = setInterval(() => {
        if (activeRef.current && Date.now() - commandStartRef.current > 15000) {
          console.warn("[Jarvis] global watchdog: force-clearing stuck listening state");
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
          activeRef.current = false;
          setListening(false);
          setTranscript("");
          // Kick the recogniser too — it may be stalled
          try { recogRef.current?.stop(); } catch {}
        }
      }, 2000);

      return () => {
        clearTimeout(restartTimerRef.current);
        clearTimeout(silenceTimerRef.current);
        clearInterval(stuckWatch);
        try { recogRef.current?.stop(); } catch {}
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("focus", onVisible);
      };
    }
    clearTimeout(restartTimerRef.current);
    clearTimeout(silenceTimerRef.current);
    try { recogRef.current?.stop(); } catch {}
    activeRef.current = false;
    setListening(false);
    setTranscript("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeArmed]);

  // --- Tap-to-speak (single shot, no wake word required) --------------------
  const activate = useCallback(async () => {
    // STEP 1 — always play a quick "I'm here, sir" so the owner at least hears
    // the voice working, even if the mic is blocked or speech recognition is
    // unavailable. This makes the orb usable as a "test the voice" button.
    if (!speaking) {
      const greetings = [
        "Right here, sir. What can I do for you?",
        "At your service, boss. Speak when you're ready.",
        "Listening, sir. Go ahead.",
        "I'm here. What's on your mind, boss?",
      ];
      speak(greetings[Math.floor(Math.random() * greetings.length)]);
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    // This is a user gesture — perfect place to (re)request permission AND unlock autoplay
    const granted = await requestMicPermission();
    if (!granted) return; // we've already given verbal feedback above
    // Unlock autoplay by playing a silent 1-frame mp3
    try {
      const a = new Audio("data:audio/mpeg;base64,SUQzAwAAAAAAFlRTU0UAAAAMAAADTGF2ZjU3LjcxLjEwMP/7kGQAAAAAAAAAAAAAAAAAAAAAAAAA");
      a.volume = 0;
      a.play().catch(() => {});
    } catch {}

    try { recogRef.current?.stop(); } catch {}

    const recog = new SR();
    recog.continuous = false;
    recog.interimResults = true;
    recog.lang = "en-GB";
    setListening(true);

    let final = "";
    recog.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += " " + t;
        else interim += t;
      }
      setTranscript((final + " " + interim).trim());
    };
    recog.onend = () => {
      setListening(false);
      setTranscript("");
      const raw = final.trim();
      const cmd = extractCommandAfterWake(raw) || raw;
      if (cmd) sendToBrain(cmd);
      if (wakeArmedRef.current) setTimeout(() => startRecognition(), 350);
    };
    recog.onerror = () => {
      setListening(false);
      if (wakeArmedRef.current) setTimeout(() => startRecognition(), 350);
    };
    recogRef.current = recog;
    try { recog.start(); } catch {}
  }, [requestMicPermission, sendToBrain, startRecognition]);

  const sendText = useCallback((text, intentHint, opts) => sendToBrain(text, intentHint, opts), [sendToBrain]);

  // --- PRESS-TO-TALK (works on iPhone, Android, every desktop browser) ------
  // Records audio via MediaRecorder, sends to backend Whisper endpoint, then
  // forwards the transcript to the brain. This is the reliable fallback for
  // platforms where SpeechRecognition either doesn't exist (iOS Safari) or
  // can't get continuous mic access.
  const recorderRef = useRef(null);
  const recorderStreamRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);

  const stopRecording = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") return;
    return new Promise((resolve) => {
      rec.onstop = async () => {
        try {
          const stream = recorderStreamRef.current;
          if (stream) stream.getTracks().forEach(t => t.stop());
        } catch {}
        recorderStreamRef.current = null;
        const chunks = recorderChunksRef.current;
        recorderChunksRef.current = [];
        if (!chunks.length) { setRecording(false); resolve(null); return; }
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        setRecording(false);
        setThinking(true);
        try {
          // Pick a sensible filename extension from mimeType so backend knows the format
          let ext = "webm";
          if (/mp4|m4a/.test(blob.type)) ext = "m4a";
          else if (/wav/.test(blob.type)) ext = "wav";
          else if (/ogg/.test(blob.type)) ext = "ogg";
          const form = new FormData();
          form.append("audio", blob, `clip.${ext}`);
          const tk = localStorage.getItem("rawco_token");
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/transcribe`, {
            method: "POST",
            headers: { Authorization: `Bearer ${tk}` },
            body: form,
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const j = await r.json();
          const text = (j.text || "").trim();
          if (text) {
            // Strip wake word if present
            const cmd = extractCommandAfterWake(text) || text;
            await sendToBrain(cmd);
          } else {
            await speak("I didn't catch that, sir — try again?");
          }
        } catch (e) {
          console.warn("[Jarvis] transcribe error:", e?.message);
          await speak("Apologies sir, my ears glitched. Try again?");
        } finally {
          setThinking(false);
          resolve(null);
        }
      };
      try { rec.stop(); } catch {}
    });
  }, [sendToBrain, speak]);

  const startRecording = useCallback(async () => {
    if (recording) return;
    // iOS standalone PWA fallback — MediaRecorder is often unsupported in
    // iOS WebKit standalone. Fall back to native SpeechRecognition immediately.
    if (typeof window.MediaRecorder === "undefined") {
      console.warn("[useJarvis] MediaRecorder missing — using native SpeechRecognition fallback (iOS)");
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        setMicStatus("error");
        speak("Your browser doesn't support voice input, sir. Use the text bar at the bottom.");
        return;
      }
      try {
        const r = new SR();
        r.lang = "en-GB";
        r.interimResults = false;
        r.maxAlternatives = 1;
        r.onresult = (ev) => {
          const transcript = ev.results?.[0]?.[0]?.transcript;
          if (transcript) sendToBrain(transcript);
        };
        r.onerror = () => setMicStatus("error");
        r.start();
        setRecording(true);
        setTimeout(() => { try { r.stop(); } catch {} ; setRecording(false); }, 8000);
        return;
      } catch (e) {
        console.warn("[useJarvis] SR fallback failed:", e);
        setMicStatus("error");
        return;
      }
    }
    try {
      // Pause continuous speech recognition while we record (so the mic isn't claimed twice)
      try { recogRef.current?.stop(); } catch {}
      // Also try to enumerate devices first to give better error info
      let deviceInfo = "unknown";
      try {
        const devs = await navigator.mediaDevices?.enumerateDevices?.();
        const mics = (devs || []).filter(d => d.kind === "audioinput");
        deviceInfo = mics.length ? `${mics.length} mic(s): ${mics.map(m => m.label || "(unlabeled)").join(", ")}` : "NO MIC DEVICES FOUND";
      } catch {}
      console.log("[Jarvis] mic devices:", deviceInfo);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderStreamRef.current = stream;
      // Choose best supported mimeType across platforms
      const candidates = [
        "audio/webm;codecs=opus", "audio/webm", "audio/mp4",
        "audio/aac", "audio/ogg;codecs=opus", "",
      ];
      let mime = "";
      for (const c of candidates) {
        if (c === "" || window.MediaRecorder?.isTypeSupported?.(c)) { mime = c; break; }
      }
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data); };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      // Auto-stop after 2s of silence so the orb knows you've finished talking
      // without you having to tap it again.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        let silenceStart = null;
        let everSpoke = false;
        const tick = () => {
          if (recorderRef.current !== rec || rec.state === "inactive") {
            try { ctx.close(); } catch {}
            return;
          }
          analyser.getByteTimeDomainData(buf);
          let max = 0;
          for (let i = 0; i < buf.length; i++) { const v = Math.abs(buf[i] - 128); if (v > max) max = v; }
          const loud = max > 8; // threshold (out of 128)
          if (loud) { everSpoke = true; silenceStart = null; }
          else if (everSpoke) {
            if (silenceStart === null) silenceStart = Date.now();
            else if (Date.now() - silenceStart > 1800) {
              // 1.8s of silence after speaking → finish
              try { ctx.close(); } catch {}
              stopRecording();
              return;
            }
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      } catch {}
    } catch (e) {
      const name = e?.name || "UnknownError";
      const msg = e?.message || "";
      console.warn("[Jarvis] startRecording failed:", name, msg);
      setMicStatus("denied");
      // Build a more helpful spoken message based on the actual error name
      let spoken;
      if (name === "NotAllowedError" || name === "SecurityError") {
        spoken = "The browser is still blocking my microphone, sir. Check the padlock in the address bar.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        spoken = "I can't find a microphone on this machine, sir. Make sure one is plugged in or that your laptop's built-in mic is enabled.";
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        spoken = "Another app has taken hold of the microphone, sir — close Teams, Zoom, or any voice call and try again.";
      } else if (name === "AbortError") {
        spoken = "The microphone connection dropped, sir. Try once more.";
      } else {
        spoken = `My microphone hit an error called ${name}, sir. Tell the dev so we can sort it.`;
      }
      await speak(spoken);
    }
  }, [recording, speak]);

  const pressToTalk = useCallback(async () => {
    if (recording) {
      await stopRecording();
    } else {
      // Soft confirmation chime so the owner knows we're listening NOW
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          const now = ctx.currentTime;
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine"; o.frequency.setValueAtTime(660, now);
          o.frequency.exponentialRampToValueAtTime(990, now + 0.12);
          g.gain.setValueAtTime(0.0001, now);
          g.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
          o.connect(g); g.connect(ctx.destination);
          o.start(now); o.stop(now + 0.2);
          setTimeout(() => { try { ctx.close(); } catch {} }, 300);
        }
      } catch {}
      await startRecording();
    }
  }, [recording, startRecording, stopRecording]);

  const stopAudio = useCallback(() => {
    try { audioRef.current?.pause(); } catch {}
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setSpeaking(false); window.__rcoJarvisSpeaking = false;
  }, []);

  return {
    listening, wakeArmed, thinking, speaking, transcript, micStatus, recording,
    muted, setMuted,
    setWakeArmed, activate, sendText, speak, stopAudio, pressToTalk, startRecording, stopRecording,
  };
}
